import {
  Badge,
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  Select,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useEffect, useMemo, useRef, useState } from "react"

import { OpenAPI } from "../../../client"

export const Route = createFileRoute("/_layout/admin/marketing-leads")({
  validateSearch: (search: Record<string, unknown>) => ({
    leadId: typeof search.leadId === "string" ? search.leadId : "",
  }),
  component: MarketingLeadsAdminPage,
})

type LeadItem = {
  id: string
  email: string
  first_name: string
  last_name: string
  company_name: string
  phone: string
  job_title: string
  country_code: string
  source: string
  notes: string
  status: string
  unsubscribed: boolean
  unsubscribed_at?: string | null
  unsubscribe_reason?: string | null
  created_at: string
  updated_at: string
}

type ListResponse = {
  items: LeadItem[]
  total: number
  limit: number
  offset: number
}

type LeadPayload = {
  email: string
  first_name: string
  last_name: string
  company_name: string
  phone: string
  job_title: string
  country_code: string
  source: string
  tags: string[]
  custom_fields: Record<string, unknown>
  notes: string
  status: string
  unsubscribed: boolean
  unsubscribe_reason: string
}

type ImportResponse = {
  inserted_rows: number
  updated_rows: number
  skipped_rows: number
  errors: Array<{ row: number; message: string }>
}

const defaultLeadForm: LeadPayload = {
  email: "",
  first_name: "",
  last_name: "",
  company_name: "",
  phone: "",
  job_title: "",
  country_code: "",
  source: "",
  tags: [],
  custom_fields: {},
  notes: "",
  status: "active",
  unsubscribed: false,
  unsubscribe_reason: "",
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = localStorage.getItem("access_token") || ""
  const res = await fetch(`${OpenAPI.BASE}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    const text = await res.text()
    try {
      const parsed = JSON.parse(text)
      throw new Error(
        parsed?.error ||
          parsed?.detail ||
          parsed?.message ||
          `HTTP ${res.status}`,
      )
    } catch {
      throw new Error(text || `HTTP ${res.status}`)
    }
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

function getLeadStatusColor(status?: string) {
  switch (status) {
    case "active":
      return "green"
    case "bounced":
      return "orange"
    case "invalid":
      return "red"
    case "archived":
      return "gray"
    default:
      return "gray"
  }
}

function getLeadStatusLabel(status?: string) {
  switch (status) {
    case "active":
      return "Aktywny"
    case "bounced":
      return "Odbity"
    case "invalid":
      return "Nieprawidłowy"
    case "archived":
      return "Archiwalny"
    default:
      return status || "Nieznany"
  }
}

function MarketingLeadsAdminPage() {
  const navigate = useNavigate()
  const search = Route.useSearch()
  const toast = useToast()
  const queryClient = useQueryClient()
  const panelBg = useColorModeValue("white", "gray.800")
  const mutedTextColor = useColorModeValue("gray.500", "gray.300")
  const tableHeadBg = useColorModeValue("gray.50", "gray.700")
  const tableRowHoverBg = useColorModeValue("gray.50", "gray.700")
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const [q, setQ] = useState("")
  const [status, setStatus] = useState("")
  const [unsubscribedFilter, setUnsubscribedFilter] = useState("")
  const [editingId, setEditingId] = useState<string>("")
  const [tagInput, setTagInput] = useState("")
  const [form, setForm] = useState<LeadPayload>(defaultLeadForm)

  const queryString = useMemo(() => {
    const params = new URLSearchParams()
    params.set("limit", "200")
    if (q.trim()) params.set("q", q.trim())
    if (status) params.set("status", status)
    if (unsubscribedFilter) params.set("unsubscribed", unsubscribedFilter)
    return params.toString()
  }, [q, status, unsubscribedFilter])

  const listQuery = useQuery({
    queryKey: ["lms", "marketing", "leads", queryString],
    queryFn: () =>
      apiFetch<ListResponse>(`/v1/lms/marketing/leads?${queryString}`),
  })

  const activeStatsQuery = useQuery({
    queryKey: ["lms", "marketing", "leads", "stats", "active"],
    queryFn: () =>
      apiFetch<ListResponse>(
        "/v1/lms/marketing/leads?limit=1&status=active&unsubscribed=false",
      ),
  })

  const unsubscribedStatsQuery = useQuery({
    queryKey: ["lms", "marketing", "leads", "stats", "unsubscribed"],
    queryFn: () =>
      apiFetch<ListResponse>(
        "/v1/lms/marketing/leads?limit=1&unsubscribed=true",
      ),
  })

  const activeCount = activeStatsQuery.data?.total ?? 0
  const unsubscribedCount = unsubscribedStatsQuery.data?.total ?? 0

  const upsertMutation = useMutation({
    mutationFn: (payload: LeadPayload) =>
      apiFetch<LeadItem>(
        editingId
          ? `/v1/lms/marketing/leads/${editingId}`
          : "/v1/lms/marketing/leads",
        {
          method: editingId ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      ),
    onSuccess: () => {
      toast({
        title: editingId
          ? "Lead został zaktualizowany"
          : "Lead został utworzony",
        status: "success",
        duration: 2500,
      })
      queryClient.invalidateQueries({ queryKey: ["lms", "marketing", "leads"] })
      setEditingId("")
      setTagInput("")
      setForm(defaultLeadForm)
    },
    onError: (error: any) => {
      toast({
        title: "Nie udało się zapisać",
        description: error?.message ?? "Błąd",
        status: "error",
        duration: 4000,
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (leadId: string) =>
      apiFetch<{ status: string }>(`/v1/lms/marketing/leads/${leadId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      toast({
        title: "Lead został usunięty",
        status: "warning",
        duration: 2500,
      })
      queryClient.invalidateQueries({ queryKey: ["lms", "marketing", "leads"] })
      if (editingId) {
        setEditingId("")
        setTagInput("")
        setForm(defaultLeadForm)
      }
    },
    onError: (error: any) => {
      toast({
        title: "Nie udało się usunąć",
        description: error?.message ?? "Błąd",
        status: "error",
        duration: 4000,
      })
    },
  })

  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append("file", file)
      return apiFetch<ImportResponse>("/v1/lms/marketing/leads/import-csv", {
        method: "POST",
        body: formData,
      })
    },
    onSuccess: (result) => {
      toast({
        title: "Zaimportowano plik CSV",
        description: `Dodano: ${result.inserted_rows}, zaktualizowano: ${result.updated_rows}, pominięto: ${result.skipped_rows}`,
        status: "success",
        duration: 5000,
      })
      queryClient.invalidateQueries({ queryKey: ["lms", "marketing", "leads"] })
    },
    onError: (error: any) => {
      toast({
        title: "Nie udało się zaimportować",
        description: error?.message ?? "Błąd",
        status: "error",
        duration: 4000,
      })
    },
  })

  const startCreate = () => {
    navigate({ to: "/admin/marketing-leads", search: { leadId: "" } })
    setEditingId("")
    setTagInput("")
    setForm(defaultLeadForm)
  }

  const startEdit = (lead: LeadItem) => {
    navigate({ to: "/admin/marketing-leads", search: { leadId: lead.id } })
    setEditingId(lead.id)
    setForm({
      email: lead.email,
      first_name: lead.first_name ?? "",
      last_name: lead.last_name ?? "",
      company_name: lead.company_name ?? "",
      phone: lead.phone ?? "",
      job_title: lead.job_title ?? "",
      country_code: lead.country_code ?? "",
      source: lead.source ?? "",
      tags: [],
      custom_fields: {},
      notes: lead.notes ?? "",
      status: lead.status ?? "active",
      unsubscribed: lead.unsubscribed,
      unsubscribe_reason: lead.unsubscribe_reason ?? "",
    })
    setTagInput("")
  }

  useEffect(() => {
    if (!search.leadId) return
    const matchedLead = (listQuery.data?.items ?? []).find(
      (item) => item.id === search.leadId,
    )
    if (matchedLead) {
      setEditingId(matchedLead.id)
      setForm({
        email: matchedLead.email,
        first_name: matchedLead.first_name ?? "",
        last_name: matchedLead.last_name ?? "",
        company_name: matchedLead.company_name ?? "",
        phone: matchedLead.phone ?? "",
        job_title: matchedLead.job_title ?? "",
        country_code: matchedLead.country_code ?? "",
        source: matchedLead.source ?? "",
        tags: [],
        custom_fields: {},
        notes: matchedLead.notes ?? "",
        status: matchedLead.status ?? "active",
        unsubscribed: matchedLead.unsubscribed,
        unsubscribe_reason: matchedLead.unsubscribe_reason ?? "",
      })
      setTagInput("")
    }
  }, [listQuery.data?.items, search.leadId])

  return (
    <Container maxW="9xl" py={4}>
      <VStack align="stretch" spacing={4}>
        <Box borderWidth="1px" borderRadius="xl" p={5} bg={panelBg}>
          <Heading size="lg">Leady marketingowe</Heading>
          <Text color={mutedTextColor} mt={1}>
            Rejestr kontaktów do cold outreach, importu, ręcznego zarządzania i
            wykluczania z wysyłek.
          </Text>
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
            gap={3}
            mt={4}
          >
            <Box borderWidth="1px" borderRadius="lg" p={3}>
              <Stat>
                <StatLabel>Załadowane leady</StatLabel>
                <StatNumber>{listQuery.data?.total ?? 0}</StatNumber>
                <StatHelpText>W zakresie bieżącego filtra</StatHelpText>
              </Stat>
            </Box>
            <Box borderWidth="1px" borderRadius="lg" p={3}>
              <Stat>
                <StatLabel>Aktywne leady</StatLabel>
                <StatNumber color={activeCount > 0 ? "green.500" : "gray.700"}>
                  {activeCount}
                </StatNumber>
                <StatHelpText>Dostępne dla kampanii</StatHelpText>
              </Stat>
            </Box>
            <Box borderWidth="1px" borderRadius="lg" p={3}>
              <Stat>
                <StatLabel>Wypisani</StatLabel>
                <StatNumber
                  color={unsubscribedCount > 0 ? "orange.500" : "gray.700"}
                >
                  {unsubscribedCount}
                </StatNumber>
                <StatHelpText>Wykluczeni z przyszłych wysyłek</StatHelpText>
              </Stat>
            </Box>
          </Grid>
        </Box>

        <Box borderWidth="1px" borderRadius="xl" p={4}>
          <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={3}>
            <GridItem>
              <FormControl>
                <FormLabel>Szukaj</FormLabel>
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="e-mail / imię / firma"
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="">Wszystkie</option>
                  <option value="active">Aktywny</option>
                  <option value="bounced">Odbity</option>
                  <option value="invalid">Nieprawidłowy</option>
                  <option value="archived">Archiwalny</option>
                </Select>
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel>Wypisanie</FormLabel>
                <Select
                  value={unsubscribedFilter}
                  onChange={(e) => setUnsubscribedFilter(e.target.value)}
                >
                  <option value="">Wszystkie</option>
                  <option value="false">tylko zapisani</option>
                  <option value="true">tylko wypisani</option>
                </Select>
              </FormControl>
            </GridItem>
            <GridItem alignSelf="end">
              <HStack>
                <Button flex="1" onClick={startCreate}>
                  Nowy lead
                </Button>
                <Button
                  flex="1"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  isLoading={importMutation.isPending}
                >
                  Importuj CSV
                </Button>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  display="none"
                  onChange={(event) => {
                    const file = event.target.files?.[0]
                    if (file) importMutation.mutate(file)
                    event.target.value = ""
                  }}
                />
              </HStack>
            </GridItem>
          </Grid>
        </Box>

        <Box borderWidth="1px" borderRadius="xl" p={4}>
          <Heading size="md" mb={4}>
            {editingId ? "Edytuj lead" : "Utwórz lead"}
          </Heading>
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(12, 1fr)" }}
            gap={3}
          >
            <GridItem colSpan={{ base: 12, md: 4 }}>
              <FormControl isRequired>
                <FormLabel>Email</FormLabel>
                <Input
                  value={form.email}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      email: e.target.value,
                    }))
                  }
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 12, md: 4 }}>
              <FormControl>
                <FormLabel>Imię</FormLabel>
                <Input
                  value={form.first_name}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      first_name: e.target.value,
                    }))
                  }
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 12, md: 4 }}>
              <FormControl>
                <FormLabel>Nazwisko</FormLabel>
                <Input
                  value={form.last_name}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      last_name: e.target.value,
                    }))
                  }
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 12, md: 4 }}>
              <FormControl>
                <FormLabel>Firma</FormLabel>
                <Input
                  value={form.company_name}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      company_name: e.target.value,
                    }))
                  }
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 12, md: 4 }}>
              <FormControl>
                <FormLabel>Telefon</FormLabel>
                <Input
                  value={form.phone}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      phone: e.target.value,
                    }))
                  }
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 12, md: 4 }}>
              <FormControl>
                <FormLabel>Stanowisko</FormLabel>
                <Input
                  value={form.job_title}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      job_title: e.target.value,
                    }))
                  }
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 12, md: 3 }}>
              <FormControl>
                <FormLabel>Kod kraju</FormLabel>
                <Input
                  value={form.country_code}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      country_code: e.target.value,
                    }))
                  }
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 12, md: 3 }}>
              <FormControl>
                <FormLabel>Źródło</FormLabel>
                <Input
                  value={form.source}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      source: e.target.value,
                    }))
                  }
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 12, md: 3 }}>
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  value={form.status}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      status: e.target.value,
                    }))
                  }
                >
                  <option value="active">Aktywny</option>
                  <option value="bounced">Odbity</option>
                  <option value="invalid">Nieprawidłowy</option>
                  <option value="archived">Archiwalny</option>
                </Select>
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 12, md: 3 }}>
              <FormControl>
                <FormLabel>Tagi</FormLabel>
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="tag1, tag2, tag3"
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={12}>
              <FormControl>
                <FormLabel>Notatki</FormLabel>
                <Textarea
                  value={form.notes}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      notes: e.target.value,
                    }))
                  }
                  rows={3}
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 12, md: 6 }}>
              <FormControl>
                <FormLabel>Wypisany</FormLabel>
                <Select
                  value={form.unsubscribed ? "true" : "false"}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      unsubscribed: e.target.value === "true",
                    }))
                  }
                >
                  <option value="false">nie</option>
                  <option value="true">tak</option>
                </Select>
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 12, md: 6 }}>
              <FormControl isDisabled={!form.unsubscribed}>
                <FormLabel>Powód wypisania</FormLabel>
                <Input
                  value={form.unsubscribe_reason}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      unsubscribe_reason: e.target.value,
                    }))
                  }
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={12}>
              <HStack justify="flex-end">
                <Button variant="ghost" onClick={startCreate}>
                  Resetuj
                </Button>
                <Button
                  colorScheme="green"
                  onClick={() =>
                    upsertMutation.mutate({
                      ...form,
                      tags: tagInput
                        .split(",")
                        .map((item) => item.trim())
                        .filter(Boolean),
                    })
                  }
                  isLoading={upsertMutation.isPending}
                >
                  {editingId ? "Zaktualizuj lead" : "Utwórz lead"}
                </Button>
              </HStack>
            </GridItem>
          </Grid>
        </Box>

        <Box borderWidth="1px" borderRadius="xl" p={4}>
          <Heading size="md" mb={4}>
            Lista leadów
          </Heading>
          {listQuery.isPending ? (
            <Spinner />
          ) : (
            <TableContainer>
              <Table size="sm">
                <Thead bg={tableHeadBg}>
                  <Tr>
                    <Th>Email</Th>
                    <Th>Imię</Th>
                    <Th>Firma</Th>
                    <Th>Status</Th>
                    <Th>Wypisanie</Th>
                    <Th>Źródło</Th>
                    <Th>Zaktualizowano</Th>
                    <Th textAlign="right">Akcje</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {(listQuery.data?.items ?? []).map((item) => (
                    <Tr key={item.id} _hover={{ bg: tableRowHoverBg }}>
                      <Td>{item.email}</Td>
                      <Td>
                        {[item.first_name, item.last_name]
                          .filter(Boolean)
                          .join(" ") || "—"}
                      </Td>
                      <Td>{item.company_name || "—"}</Td>
                      <Td>
                        <Badge colorScheme={getLeadStatusColor(item.status)}>
                          {getLeadStatusLabel(item.status)}
                        </Badge>
                      </Td>
                      <Td>
                        {item.unsubscribed ? (
                          <Badge colorScheme="orange">wypisany</Badge>
                        ) : (
                          <Badge colorScheme="green">zapisany</Badge>
                        )}
                      </Td>
                      <Td>{item.source || "—"}</Td>
                      <Td>{new Date(item.updated_at).toLocaleString()}</Td>
                      <Td>
                        <HStack justify="flex-end">
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() => startEdit(item)}
                          >
                            Edytuj
                          </Button>
                          <Button
                            size="xs"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => deleteMutation.mutate(item.id)}
                            isLoading={deleteMutation.isPending}
                          >
                            Usuń
                          </Button>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </VStack>
    </Container>
  )
}
